import React, { useState } from 'react';

const InstallerList = ({ 
  installers, 
  selectedInstallers, 
  onSelectInstaller, 
  onSelectAll, 
  onViewDetails, 
  onExport 
}) => {
  const [sortBy, setSortBy] = useState('company_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showExportModal, setShowExportModal] = useState(false);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const sortedInstallers = [...installers].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];
    
    // Handle null/undefined values
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';
    
    // Convert to strings for comparison
    aValue = String(aValue).toLowerCase();
    bValue = String(bValue).toLowerCase();
    
    if (sortOrder === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  const handleExport = (format) => {
    onExport(format);
    setShowExportModal(false);
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getQualityScoreColor = (score) => {
    if (score >= 90) return '#00E676';
    if (score >= 80) return '#FFD600';
    if (score >= 70) return '#FF9100';
    return '#FF6B6B';
  };

  const styles = {
    container: {
      backgroundColor: 'rgba(13, 27, 49, 0.85)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '20px',
      padding: '2rem',
      marginTop: '2rem',
      backdropFilter: 'blur(20px)'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem'
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#FFFFFF',
      margin: 0
    },
    summary: {
      fontSize: '0.9rem',
      color: '#B0C4DE',
      marginBottom: '1rem'
    },
    actions: {
      display: 'flex',
      gap: '1rem',
      alignItems: 'center'
    },
    button: {
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      border: 'none'
    },
    primaryButton: {
      backgroundColor: '#2979FF',
      color: '#FFFFFF',
      boxShadow: '0 4px 15px rgba(41, 121, 255, 0.3)'
    },
    secondaryButton: {
      backgroundColor: 'transparent',
      color: '#B0C4DE',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    },
    tableContainer: {
      overflowX: 'auto',
      borderRadius: '12px',
      border: '1px solid rgba(255, 255, 255, 0.1)'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      backgroundColor: 'rgba(255, 255, 255, 0.02)'
    },
    th: {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      padding: '1rem',
      textAlign: 'left',
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#B0C4DE',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      cursor: 'pointer',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      whiteSpace: 'nowrap'
    },
    td: {
      padding: '1rem',
      fontSize: '0.9rem',
      color: '#FFFFFF',
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
      verticalAlign: 'middle'
    },
    tr: {
      transition: 'background-color 0.2s ease',
      cursor: 'pointer'
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer'
    },
    companyName: {
      fontWeight: '600',
      color: '#FFFFFF',
      minWidth: '180px'
    },
    location: {
      color: '#B0C4DE',
      minWidth: '120px'
    },
    phone: {
      color: '#FFFFFF',
      fontFamily: 'monospace',
      minWidth: '120px'
    },
    qualityScore: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      minWidth: '100px'
    },
    scoreBadge: {
      padding: '0.25rem 0.5rem',
      borderRadius: '6px',
      fontSize: '0.8rem',
      fontWeight: '600',
      color: '#000000'
    },
    installations: {
      color: '#FFFFFF',
      fontWeight: '600',
      minWidth: '100px'
    },
    lastActivity: {
      color: '#B0C4DE',
      fontSize: '0.8rem',
      minWidth: '100px'
    },
    viewButton: {
      backgroundColor: '#2979FF',
      color: '#FFFFFF',
      border: 'none',
      borderRadius: '6px',
      padding: '0.5rem 1rem',
      fontSize: '0.8rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    sortIcon: {
      marginLeft: '0.5rem',
      fontSize: '0.8rem'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modalContent: {
      backgroundColor: 'rgba(13, 27, 49, 0.95)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '16px',
      padding: '2rem',
      minWidth: '400px',
      backdropFilter: 'blur(20px)'
    },
    modalTitle: {
      fontSize: '1.3rem',
      fontWeight: '700',
      color: '#FFFFFF',
      marginBottom: '1rem'
    },
    modalText: {
      color: '#B0C4DE',
      marginBottom: '1.5rem',
      fontSize: '0.9rem'
    },
    modalActions: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'flex-end'
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Search Results</h2>
          <p style={styles.summary}>
            Showing {installers.length} results
            {selectedInstallers.length > 0 && ` • ${selectedInstallers.length} selected`}
          </p>
        </div>
        <div style={styles.actions}>
          <button
            style={{ ...styles.button, ...styles.secondaryButton }}
            onClick={onSelectAll}
          >
            {selectedInstallers.length === installers.length ? 'Deselect All' : 'Select All'}
          </button>
          <button
            style={{ 
              ...styles.button, 
              ...styles.primaryButton,
              opacity: selectedInstallers.length === 0 ? 0.5 : 1
            }}
            onClick={() => setShowExportModal(true)}
            disabled={selectedInstallers.length === 0}
          >
            Export Selected ({selectedInstallers.length})
          </button>
        </div>
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>
                <input
                  type="checkbox"
                  style={styles.checkbox}
                  checked={selectedInstallers.length === installers.length}
                  onChange={onSelectAll}
                />
              </th>
              <th style={styles.th} onClick={() => handleSort('company_name')}>
                Company Name
                <span style={styles.sortIcon}>{getSortIcon('company_name')}</span>
              </th>
              <th style={styles.th} onClick={() => handleSort('city')}>
                Location
                <span style={styles.sortIcon}>{getSortIcon('city')}</span>
              </th>
              <th style={styles.th}>Phone</th>
              <th style={styles.th} onClick={() => handleSort('data_quality_score')}>
                Quality Score
                <span style={styles.sortIcon}>{getSortIcon('data_quality_score')}</span>
              </th>
              <th style={styles.th} onClick={() => handleSort('total_installations')}>
                Installations
                <span style={styles.sortIcon}>{getSortIcon('total_installations')}</span>
              </th>
              <th style={styles.th} onClick={() => handleSort('last_installation_date')}>
                Last Activity
                <span style={styles.sortIcon}>{getSortIcon('last_installation_date')}</span>
              </th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedInstallers.map((installer) => (
              <tr
                key={installer.id}
                style={styles.tr}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <td style={styles.td}>
                  <input
                    type="checkbox"
                    style={styles.checkbox}
                    checked={selectedInstallers.includes(installer.id)}
                    onChange={() => onSelectInstaller(installer.id)}
                  />
                </td>
                <td style={{ ...styles.td, ...styles.companyName }}>
                  <div>
                    <div>{installer.company_name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#7F8C9A', marginTop: '0.25rem' }}>
                      {installer.license_type} • {installer.license_number}
                    </div>
                  </div>
                </td>
                <td style={{ ...styles.td, ...styles.location }}>
                  <div>
                    <div>{installer.city}, {installer.state}</div>
                    <div style={{ fontSize: '0.8rem', color: '#7F8C9A', marginTop: '0.25rem' }}>
                      {installer.county} County
                    </div>
                  </div>
                </td>
                <td style={{ ...styles.td, ...styles.phone }}>
                  {installer.primary_phone || 'N/A'}
                </td>
                <td style={{ ...styles.td, ...styles.qualityScore }}>
                  <div
                    style={{
                      ...styles.scoreBadge,
                      backgroundColor: getQualityScoreColor(installer.data_quality_score)
                    }}
                  >
                    {installer.data_quality_score || 0}
                  </div>
                </td>
                <td style={{ ...styles.td, ...styles.installations }}>
                  <div>
                    <div>{formatNumber(installer.total_installations)}</div>
                    <div style={{ fontSize: '0.8rem', color: '#7F8C9A', marginTop: '0.25rem' }}>
                      {installer.total_mw_installed?.toFixed(1) || 0} MW
                    </div>
                  </div>
                </td>
                <td style={{ ...styles.td, ...styles.lastActivity }}>
                  {formatDate(installer.last_installation_date)}
                </td>
                <td style={styles.td}>
                  <button
                    style={styles.viewButton}
                    onClick={() => onViewDetails(installer.id)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#1E40AF';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#2979FF';
                    }}
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>Export Leads</h3>
            <p style={styles.modalText}>
              You have selected {selectedInstallers.length} leads to export.
            </p>
            <p style={styles.modalText}>
              Select Format:
            </p>
            <div style={styles.modalActions}>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={() => setShowExportModal(false)}
              >
                Cancel
              </button>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={() => handleExport('csv')}
              >
                Download CSV
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstallerList; 